/**
 * Classificação da oportunidade (RN-080 a RN-093).
 *
 * Devolve SEMPRE todos os motivos avaliados, atendidos e não atendidos, com o
 * identificador da regra — a transparência exigida pela RN-093. A tela não
 * escolhe o que mostrar: mostra tudo.
 */

import type {
  Classificacao,
  ContextoClassificacao,
  Criterios,
  Motivo,
  ResultadoAnalise,
  ResultadoClassificacao,
} from './tipos.js';
import { formatarPct, formatarReais, formatarQtd } from './inteiros.js';

export function classificar(
  r: ResultadoAnalise,
  c: Criterios,
  ctx: ContextoClassificacao,
  quantidadePretendidaMilli: number,
): ResultadoClassificacao {
  const motivos: Motivo[] = [];

  // --- RN-051 / CA-51: parâmetro não configurado nunca vira zero ---
  if (!r.completo) {
    motivos.push({
      regra: 'RN-051',
      texto: `Parâmetros obrigatórios não configurados: ${r.faltando.join(', ')}`,
      atende: false,
    });
    return { classificacao: 'DADOS_INSUFICIENTES', motivos };
  }

  // --- RN-085: anúncios comparáveis suficientes ---
  const anunciosOk = ctx.qtdAnunciosValidos >= c.minAnunciosComp;
  motivos.push({
    regra: 'RN-085',
    texto: `${ctx.qtdAnunciosValidos} anúncio(s) comparável(is); o mínimo é ${c.minAnunciosComp}`,
    atende: anunciosOk,
  });

  // --- RN-092: lucro unitário positivo ---
  const lucroOk = r.lucroUnitarioCents > 0;
  motivos.push({
    regra: 'RN-092',
    texto: `Lucro unitário de ${formatarReais(r.lucroUnitarioCents)}`,
    atende: lucroOk,
  });

  // --- RN-081 / RN-082: margem e ROI mínimos ---
  const margemOk = r.margemBps >= c.margemMinimaBps;
  motivos.push({
    regra: 'RN-081',
    texto: `Margem de ${formatarPct(r.margemBps)}; o mínimo é ${formatarPct(c.margemMinimaBps)}`,
    atende: margemOk,
  });
  const roiOk = r.roiBps >= c.roiMinimoBps;
  motivos.push({
    regra: 'RN-082',
    texto: `ROI de ${formatarPct(r.roiBps)}; o mínimo é ${formatarPct(c.roiMinimoBps)}`,
    atende: roiOk,
  });

  // --- RN-087: dispersão alta demais para uma referência única ---
  let dispersaoOk = true;
  if (ctx.menorAnuncioValidoCents !== null && ctx.maiorAnuncioValidoCents !== null && ctx.menorAnuncioValidoCents > 0) {
    const disp = Math.round(
      ((ctx.maiorAnuncioValidoCents - ctx.menorAnuncioValidoCents) * 10_000) / ctx.menorAnuncioValidoCents,
    );
    dispersaoOk = disp <= c.dispersaoMaximaBps;
    motivos.push({
      regra: 'RN-087',
      texto: `Dispersão de ${formatarPct(disp)} entre menor e maior anúncio; o limite é ${formatarPct(c.dispersaoMaximaBps)}`,
      atende: dispersaoOk,
    });
  }

  // --- RN-089: idade da cotação ---
  let cotacaoOk = true;
  if (ctx.idadeCotacaoDias !== null) {
    cotacaoOk = ctx.idadeCotacaoDias <= c.idadeMaxCotacaoDias;
    motivos.push({
      regra: 'RN-089',
      texto: `Cotação com ${ctx.idadeCotacaoDias} dia(s); o máximo é ${c.idadeMaxCotacaoDias}`,
      atende: cotacaoOk,
    });
  }

  // --- Ressalvas: não reprovam, mas rebaixam ---
  const ressalvas: Motivo[] = [];

  const capitalOk = r.capitalNecessarioCents <= c.capitalMaximoCents;
  const mCapital: Motivo = {
    regra: 'RN-083',
    texto: `Capital necessário de ${formatarReais(r.capitalNecessarioCents)}; o teto é ${formatarReais(c.capitalMaximoCents)}`,
    atende: capitalOk,
  };
  motivos.push(mCapital);
  if (!capitalOk) ressalvas.push(mCapital);

  if (ctx.prazoRetornoDias !== null) {
    const prazoOk = ctx.prazoRetornoDias <= c.prazoMaxRetornoDias;
    const m: Motivo = {
      regra: 'RN-084',
      texto: `Prazo de retorno de ${ctx.prazoRetornoDias} dia(s); o máximo é ${c.prazoMaxRetornoDias}`,
      atende: prazoOk,
    };
    motivos.push(m);
    if (!prazoOk) ressalvas.push(m);
  }

  // --- RN-086: indicador sem base de cálculo nunca vira zero ---
  if (ctx.giroEstoqueDias === null) {
    const m: Motivo = {
      regra: 'RN-086',
      texto: 'Giro de estoque indisponível: são necessárias ao menos duas coletas do mesmo anúncio',
      atende: false,
      indeterminado: true,
    };
    motivos.push(m);
    ressalvas.push(m);
  }

  // --- RN-088: cenário pessimista abaixo do ROI mínimo limita a classificação ---
  if (ctx.roiPessimistaBps !== null) {
    const pessimistaOk = ctx.roiPessimistaBps >= c.roiMinimoBps;
    const m: Motivo = {
      regra: 'RN-088',
      texto: `ROI no cenário pessimista de ${formatarPct(ctx.roiPessimistaBps)}; o mínimo é ${formatarPct(c.roiMinimoBps)}`,
      atende: pessimistaOk,
    };
    motivos.push(m);
    if (!pessimistaOk) ressalvas.push(m);
  }

  // --- RN-090: estoque do fornecedor menor que a quantidade pretendida ---
  if (ctx.estoqueDisponivelMilli !== null) {
    const estoqueOk = ctx.estoqueDisponivelMilli >= quantidadePretendidaMilli;
    const m: Motivo = {
      regra: 'RN-090',
      texto: `Estoque de ${formatarQtd(ctx.estoqueDisponivelMilli)} para ${formatarQtd(quantidadePretendidaMilli)} pretendida(s)`,
      atende: estoqueOk,
    };
    motivos.push(m);
    if (!estoqueOk) ressalvas.push(m);
  }

  // --- Precedência ---
  const classificacao: Classificacao = !anunciosOk
    ? 'DADOS_INSUFICIENTES'
    : !lucroOk
      ? 'INVIAVEL'
      : !dispersaoOk || !cotacaoOk
        ? 'REQUER_REVISAO'
        : !margemOk || !roiOk
          ? 'MARGEM_INSUFICIENTE'
          : ressalvas.length > 0
            ? 'VIAVEL_COM_RESSALVAS'
            : r.margemBps >= c.margemExcelenteBps && r.roiBps >= c.roiExcelenteBps
              ? 'EXCELENTE'
              : 'VIAVEL';

  return { classificacao, motivos };
}

/** Critérios de partida. Nenhum deles é lei: todos são configuráveis pelo Administrador. */
export const CRITERIOS_PADRAO: Criterios = {
  margemMinimaBps: 1_500, // 15,00%
  roiMinimoBps: 2_500, // 25,00%
  capitalMaximoCents: 1_000_000, // R$ 10.000,00
  prazoMaxRetornoDias: 90,
  minAnunciosComp: 3,
  idadeMaxCotacaoDias: 30,
  estoqueMinimoMilli: 50_000, // 50 unidades
  margemExcelenteBps: 3_000, // 30,00%
  roiExcelenteBps: 6_000, // 60,00%
  dispersaoMaximaBps: 4_000, // 40,00%
};
